import { render } from 'preact';
import { ScheduleList } from './components/schedule-list';
import stylesText from './styles.css?raw';

/**
 * Embed bundle entry — self-executing IIFE.
 *
 * <p>This file is the first thing the browser runs after fetching
 * {@code widget.crm.dance/v1/schedule.js}. Its responsibilities:
 *
 * <ol>
 *   <li>Idempotency — both global ({@link __crmScheduleWidgetLoaded}) and
 *       per-element ({@code host.__crmMounted}) so re-execution and
 *       double-mount are both safe.</li>
 *   <li>Container discovery — find every
 *       {@code [data-crm-widget="schedule"]} on the page, plus a
 *       fallback that mounts after {@code document.currentScript} for
 *       site builders that strip siblings of {@code <script>}.</li>
 *   <li>Late-arriving containers — a {@link MutationObserver} watches the
 *       document for newly-inserted matching nodes (host SPA navigation,
 *       Wix preview re-render) and mounts them too. Removed nodes
 *       trigger their stored {@code __crmTeardown} so in-flight fetches
 *       abort.</li>
 *   <li>Shadow DOM mount — open mode with {@code adoptedStyleSheets}
 *       (or a {@code <style>} fallback), and a Preact {@link render} into
 *       the shadow root.</li>
 * </ol>
 *
 * @see docs/specs/schedule-widget-embed/technical.md § Mounting and Shadow DOM
 */

const SLUG_REGEX = /^[a-z0-9-]{2,64}$/;
const LOCATION_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Marker on {@code window} to short-circuit re-execution if the IIFE runs twice. */
declare global {
	interface Window {
		__crmScheduleWidgetLoaded?: true;
	}
}

type WidgetHost = HTMLElement & {
	__crmMounted?: true;
	__crmTeardown?: () => void;
};

(function bootstrap(): void {
	if (window.__crmScheduleWidgetLoaded) {
		// IIFE re-ran (e.g., script tag added programmatically). Re-scan the
		// document for any containers the prior run didn't see.
		boot();
		return;
	}
	window.__crmScheduleWidgetLoaded = true;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot, { once: true });
	} else {
		boot();
	}
	observeLateContainers();
})();

function boot(): void {
	const containers = document.querySelectorAll<HTMLElement>('[data-crm-widget="schedule"]');
	containers.forEach(mountWidget);

	if (containers.length === 0) {
		// Single-element fallback — some builders allow only one block per insert,
		// so users can paste just <script src="..." data-slug="..."> and we mount
		// adjacent. document.currentScript is the original script tag during
		// IIFE execution; null inside DOMContentLoaded callbacks, so we also
		// search by src as a defensive backup.
		const cs = (document.currentScript ??
			document.querySelector('script[src*="/v1/schedule.js"][data-slug]')) as
			| (HTMLScriptElement & { dataset: DOMStringMap })
			| null;
		if (cs?.dataset.slug) {
			const placeholder = document.createElement('div');
			placeholder.dataset.crmWidget = 'schedule';
			placeholder.dataset.slug = cs.dataset.slug;
			cs.after(placeholder);
			mountWidget(placeholder);
		}
	}
}

function mountWidget(host: HTMLElement): void {
	const widgetHost = host as WidgetHost;
	if (widgetHost.__crmMounted) return;

	const slug = host.dataset.slug ?? '';
	if (!SLUG_REGEX.test(slug)) {
		// Don't render — but don't mark __crmMounted either, so a later
		// dataset-slug update + observer pass can retry. Leave a console
		// warning to help studios who paste a malformed snippet.
		// eslint-disable-next-line no-console
		console.warn(
			`[crm-schedule] invalid data-slug ${JSON.stringify(slug)} — must match ${SLUG_REGEX}`,
		);
		return;
	}
	if (typeof host.attachShadow !== 'function') {
		// eslint-disable-next-line no-console
		console.warn('[crm-schedule] this browser does not support Shadow DOM; widget not mounted');
		return;
	}

	widgetHost.__crmMounted = true;

	const shadow = host.attachShadow({ mode: 'open' });
	attachStyles(shadow);

	const root = document.createElement('div');
	root.className = 'crm-root';
	shadow.appendChild(root);

	render(<ScheduleList slug={slug} hostRef={host} locationId={locationIdOf(host)} />, root);
}

// data-location="<id>" (the id a booking page ?location= link carries) shows one location's classes.
function locationIdOf(host: HTMLElement): string | undefined {
	const id = host.dataset.location?.trim().toLowerCase();
	return id && LOCATION_ID_REGEX.test(id) ? id : undefined;
}

function attachStyles(shadow: ShadowRoot): void {
	if ('adoptedStyleSheets' in Document.prototype && typeof CSSStyleSheet === 'function') {
		try {
			const sheet = new CSSStyleSheet();
			sheet.replaceSync(stylesText);
			shadow.adoptedStyleSheets = [sheet];
			return;
		} catch {
			// Fall through to <style> tag.
		}
	}
	const style = document.createElement('style');
	style.textContent = stylesText;
	shadow.appendChild(style);
}

function observeLateContainers(): void {
	if (typeof MutationObserver !== 'function') return;
	const mo = new MutationObserver((records) => {
		for (const r of records) {
			r.addedNodes.forEach((n) => {
				if (!(n instanceof HTMLElement)) return;
				if (n.matches('[data-crm-widget="schedule"]')) mountWidget(n);
				n.querySelectorAll<HTMLElement>('[data-crm-widget="schedule"]').forEach(mountWidget);
			});
			r.removedNodes.forEach((n) => {
				if (!(n instanceof HTMLElement)) return;
				teardownIfWidget(n);
				n.querySelectorAll<HTMLElement>('[data-crm-widget="schedule"]').forEach(teardownIfWidget);
			});
		}
	});
	mo.observe(document.body ?? document.documentElement, { childList: true, subtree: true });
}

function teardownIfWidget(el: HTMLElement): void {
	const widgetHost = el as WidgetHost;
	widgetHost.__crmTeardown?.();
}
